import { Button, Checkbox, Modal } from "@etudescarre/ui";
import { useState } from "react";

export type DiffusionTarget = "do" | "presta";

export type DiffusionDialogProps = {
  open: boolean;
  onClose: () => void;
  documentName: string;
  versionIndex: string | null;
  onConfirm: (targets: DiffusionTarget[], avisRequis: boolean) => void;
  loading?: boolean;
};

const TARGET_OPTIONS: { value: DiffusionTarget; label: string }[] = [
  { value: "do", label: "Donneurs d'ordre" },
  { value: "presta", label: "Prestataires" },
];

/**
 * "Mettre en diffusion": puts a version in the waiting list of the chosen
 * targets (screen 019, screen 020, or both). Recipients are proposed later,
 * when the document enters the bordereau.
 */
export function DiffusionDialog({
  open,
  onClose,
  documentName,
  versionIndex,
  onConfirm,
  loading = false,
}: DiffusionDialogProps) {
  const [targets, setTargets] = useState<Set<DiffusionTarget>>(new Set());
  const [avisRequis, setAvisRequis] = useState(false);

  const toggleTarget = (target: DiffusionTarget) => {
    setTargets((prev) => {
      const next = new Set(prev);
      if (next.has(target)) {
        next.delete(target);
      } else {
        next.add(target);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    onConfirm(
      TARGET_OPTIONS.map((o) => o.value).filter((t) => targets.has(t)),
      avisRequis,
    );
  };

  return (
    <Modal.Root
      open={open}
      onOpenChange={(e) => {
        if (!e.open) {
          onClose();
          setTargets(new Set());
          setAvisRequis(false);
        }
      }}
    >
      <Modal.Backdrop />
      <Modal.Positioner>
        <Modal.Content size="lg">
          <Modal.Header>
            <Modal.Title>Ajouter a la liste de diffusion</Modal.Title>
          </Modal.Header>

          <div className="px-6 pb-6">
            <p className="mb-1 text-sm text-zinc-600 dark:text-zinc-400">
              {documentName}
              {versionIndex ? ` - Indice ${versionIndex}` : ""}
            </p>

            <div className="mt-4">
              <h4 className="mb-2 text-xs font-medium uppercase tracking-wide text-zinc-500">
                Diffuser vers
              </h4>
              <div className="space-y-1">
                {TARGET_OPTIONS.map((option) => (
                  <div key={option.value} className="px-2 py-1.5">
                    <Checkbox
                      checked={targets.has(option.value)}
                      onCheckedChange={() => toggleTarget(option.value)}
                    >
                      {option.label}
                    </Checkbox>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4 border-t border-zinc-200 px-2 pt-4 dark:border-zinc-700">
              <Checkbox
                checked={avisRequis}
                onCheckedChange={() => setAvisRequis(!avisRequis)}
              >
                Avis requis
              </Checkbox>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <Button onClick={onClose} className="text-sm">
                Annuler
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={targets.size === 0 || loading}
                className="text-sm"
              >
                {loading ? "En cours..." : "Ajouter"}
              </Button>
            </div>
          </div>
        </Modal.Content>
      </Modal.Positioner>
    </Modal.Root>
  );
}
